// Client-side image compression for phones on slow, expensive data. Photos and signatures are
// shrunk before they are stored or sent: staff uploads (max ~1600 px, JPEG 0.8, 2 MB) and the
// partner link's delivery photo (500 KB). No Firebase, no React: both phones apps share it.

export interface ImageHandle {
  readonly width: number;
  readonly height: number;
  /** Draws the picture at `width` x `height` and encodes it. */
  render(width: number, height: number, type: ImageType, quality: number): Promise<Blob>;
  close(): void;
}

/** Decodes and encodes pictures. The default uses the browser's canvas; tests pass a stub. */
export interface ImageEngine {
  open(blob: Blob): Promise<ImageHandle>;
}

export type ImageType = 'image/jpeg' | 'image/png';

export interface CompressOptions {
  /** Longest side in pixels. Default 1600. */
  maxSide?: number;
  /** JPEG quality to start from, 0 to 1. Default 0.8. */
  quality?: number;
  /** The result must not be bigger. Default 2 MB. */
  maxBytes?: number;
  /** `image/png` keeps a signature's lines crisp. Anything else becomes JPEG. Default JPEG. */
  type?: ImageType;
  /** Never go below this JPEG quality; shrink the picture instead. Default 0.4. */
  minQuality?: number;
  engine?: ImageEngine;
}

export interface CompressedImage {
  blob: Blob;
  type: ImageType;
  /** File extension for the type: `jpg` or `png`. */
  ext: 'jpg' | 'png';
  width: number;
  height: number;
}

export const DEFAULT_MAX_SIDE = 1600;
export const DEFAULT_QUALITY = 0.8;
export const TWO_MB = 2 * 1024 * 1024;

/** The picture could not be made small enough, or is not a picture. `message` is safe to show. */
export class ImageError extends Error {
  readonly code: 'not_image' | 'too_large' | 'unsupported';
  constructor(code: ImageError['code'], message: string) {
    super(message);
    this.name = 'ImageError';
    this.code = code;
  }
}

export function extensionOf(type: ImageType): 'jpg' | 'png' {
  return type === 'image/png' ? 'png' : 'jpg';
}

type Surface = {
  getContext(id: '2d'): CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
};

/** The browser's canvas. Chrome on Android and every current phone browser have it. */
export const canvasEngine: ImageEngine = {
  async open(blob) {
    if (typeof createImageBitmap !== 'function') {
      throw new ImageError('unsupported', 'This phone cannot shrink pictures.');
    }
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(blob);
    } catch {
      throw new ImageError('not_image', 'This file is not a picture the phone can read.');
    }
    return {
      width: bitmap.width,
      height: bitmap.height,
      async render(width, height, type, quality) {
        const useOffscreen = typeof OffscreenCanvas === 'function';
        const canvas = useOffscreen
          ? new OffscreenCanvas(width, height)
          : Object.assign(document.createElement('canvas'), { width, height });
        const ctx = (canvas as Surface).getContext('2d');
        if (!ctx) throw new ImageError('unsupported', 'This phone cannot shrink pictures.');
        if (type === 'image/jpeg') {
          // JPEG has no transparency: a signature would turn black.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(bitmap, 0, 0, width, height);
        if (canvas instanceof OffscreenCanvas) return canvas.convertToBlob({ type, quality });
        const html = canvas as HTMLCanvasElement;
        return new Promise<Blob>((resolve, reject) =>
          html.toBlob(
            (out) =>
              out ? resolve(out) : reject(new ImageError('unsupported', 'Encoding failed.')),
            type,
            quality,
          ),
        );
      },
      close: () => bitmap.close(),
    };
  },
};

const MAX_ATTEMPTS = 10;

/**
 * Shrinks a picture to at most `maxSide` pixels and `maxBytes`. It first lowers the JPEG quality
 * in steps of 0.1 down to `minQuality`, then shrinks the picture by a fifth each time, so a very
 * big photo still ends up under the limit. A picture that is already small enough is returned as
 * it came.
 */
export async function compressImage(
  input: Blob,
  options: CompressOptions = {},
): Promise<CompressedImage> {
  const {
    maxSide = DEFAULT_MAX_SIDE,
    quality: startQuality = DEFAULT_QUALITY,
    maxBytes = TWO_MB,
    minQuality = 0.4,
    engine = canvasEngine,
  } = options;
  if (!input.type.startsWith('image/')) {
    throw new ImageError('not_image', 'Only pictures can be sent.');
  }
  const type: ImageType = options.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const handle = await engine.open(input);
  try {
    const longest = Math.max(handle.width, handle.height);
    if (longest <= maxSide && input.size <= maxBytes && input.type === type) {
      return {
        blob: input,
        type,
        ext: extensionOf(type),
        width: handle.width,
        height: handle.height,
      };
    }
    let scale = Math.min(1, maxSide / Math.max(1, longest));
    let quality = startQuality;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      const width = Math.max(1, Math.round(handle.width * scale));
      const height = Math.max(1, Math.round(handle.height * scale));
      const out = await handle.render(width, height, type, quality);
      if (out.size <= maxBytes) {
        return { blob: out, type, ext: extensionOf(type), width, height };
      }
      // PNG ignores quality, so only the size can change.
      if (type === 'image/jpeg' && quality - 0.1 >= minQuality - 1e-9) {
        quality = Math.round((quality - 0.1) * 100) / 100;
      } else {
        scale *= 0.8;
      }
    }
    throw new ImageError(
      'too_large',
      'The picture is too big to send. Take it again from further away.',
    );
  } finally {
    handle.close();
  }
}
